import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { canShowPermission } from "../Permissions/permissionObject";
import {
  bulkDiamondPointGiftByFileRequest,
  bulkDiamondPointGiftByPhoneRequest,
} from "../../../redux/slices/settingSlice";

const BulkDiamondPoints = () => {
  const dispatch = useDispatch();

  const [bulkGiftType, setBulkGiftType] = useState("day");
  const [bulkGiftUnit, setBulkGiftUnit] = useState("");
  const [bulkGiftNote, setBulkGiftNote] = useState("");
  const [bulkGiftFile, setBulkGiftFile] = useState(null);
  const [bulkMobileList, setBulkMobileList] = useState([]);
  const [bulkMobile, setBulkMobile] = useState("");
  const [bulkPoints, setBulkPoints] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState({ mobile: "", points: "" });
  const [uploadMode, setUploadMode] = useState("file");
  const [isSavingBulkGift, setIsSavingBulkGift] = useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateSetting = canShowPermission(permissions, "bulkDiamondPoints", "create");
  const canEdit = canShowPermission(permissions, "bulkDiamondPoints", "edit");

  const MIN_PHONE_LENGTH = 5;
  const MAX_PHONE_LENGTH = 13;

  const handleBulkRemoveMobile = (mobile) => {
    setBulkMobileList((prev) => prev.filter((n) => n.mobile !== mobile));
  };

  const handleBulkAddMobile = () => {
    const cleanedMobile = bulkMobile.replace(/\D/g, "").replace(/^0+/, "");
    const isDuplicate = bulkMobileList.some(
      (item) => item.mobile === cleanedMobile
    );
    const isValidMobile =
      cleanedMobile.length >= MIN_PHONE_LENGTH &&
      cleanedMobile.length <= MAX_PHONE_LENGTH;
    const isValidPoints = bulkPoints.trim() !== "" && !isNaN(bulkPoints);

    if (isValidMobile && isValidPoints && !isDuplicate) {
      setBulkMobileList((prev) => [
        ...prev,
        { mobile: cleanedMobile, points: Number(bulkPoints) },
      ]);
      setBulkMobile("");
      setBulkPoints("");
    } else if (!isValidMobile) {
      toast.error(
        `Mobile number must be between ${MIN_PHONE_LENGTH} and ${MAX_PHONE_LENGTH} digits`
      );
    } else if (isDuplicate) {
      toast.error("Duplicate mobile number");
    } else if (!isValidPoints) {
      toast.error("Points must be a valid number");
    }
  };

  const handleEdit = (index) => {
    setEditIndex(index);
    setEditData({ ...bulkMobileList[index] });
  };

  const handleEditChange = (field, value) => {
    if (field === "mobile") {
      value = value
        .replace(/\D/g, "")
        .replace(/^0+/, "")
        .slice(0, MAX_PHONE_LENGTH);
    } else {
      value = value.replace(/\D/g, "");
    }
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = () => {
    const isDuplicate = bulkMobileList.some(
      (item, idx) => idx !== editIndex && item.mobile === editData.mobile
    );
    if (
      editData.mobile.length < MIN_PHONE_LENGTH ||
      editData.mobile.length > MAX_PHONE_LENGTH ||
      isDuplicate
    )
      return;

    setBulkMobileList((prev) =>
      prev.map((item, idx) => (idx === editIndex ? editData : item))
    );
    setEditIndex(null);
    setEditData({ mobile: "", points: "" });
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditData({ mobile: "", points: "" });
  };

  const handleBulkDiamondGiftByFileSubmit = async () => {
    if (isSavingBulkGift) return;

    const trimmedUnit = String(bulkGiftUnit || "").trim();
    const trimmedType = String(bulkGiftType || "").trim();

    if (!trimmedUnit || !trimmedType || !bulkGiftFile) {
      toast.error("Please fill all required fields for file upload");
      return;
    }

    setIsSavingBulkGift(true);

    try {
      let bulkGiftNote1 = bulkGiftNote || "";
      await dispatch(
        bulkDiamondPointGiftByFileRequest({
          trimmedType,
          trimmedUnit,
          bulkGiftNote1,
          bulkGiftFile,
        })
      );
      toast.success("Bulk Diamond Gift uploaded successfully!");

      setBulkGiftFile(null);
      setBulkGiftType("day");
      setBulkGiftNote("");
      setBulkGiftUnit("");
    } catch (err) {
      console.error("Failed to upload bulk gift:", err);
      toast.error("Failed to upload bulk diamond gift.");
    } finally {
      setTimeout(() => setIsSavingBulkGift(false), 500);
    }
  };

  const handleBulkDiamondGiftByPhoneSubmit = async () => {
    if (isSavingBulkGift) return;

    const trimmedUnit = String(bulkGiftUnit || "").trim();
    const trimmedType = String(bulkGiftType || "").trim();

    if (!bulkMobileList.length || !trimmedUnit || !trimmedType) {
      toast.error("Please fill all required fields for phone upload");
      return;
    }

    setIsSavingBulkGift(true);

    const data = {
      bulkGiftUnit: Number(trimmedUnit),
      bulkGiftType: trimmedType,
      bulkGiftNote: bulkGiftNote || "",
      bulkMobileList,
    };

    try {
      await dispatch(bulkDiamondPointGiftByPhoneRequest(data));
      toast.success("Bulk Diamond Gift sent successfully!");

      setBulkMobileList([]);
      setBulkGiftUnit("");
      setBulkGiftType("day");
      setBulkGiftNote("");
      setBulkMobile("");
      setBulkPoints("");
    } catch (err) {
      console.error("Failed to send bulk gift:", err);
      toast.error("Failed to send bulk diamond gift.");
    } finally {
      setTimeout(() => setIsSavingBulkGift(false), 500);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: { xs: 10, md: 10 }, mb: { xs: 2, md: 2 } }}>
      <Paper
        elevation={2}
        sx={{ p: 3, width: "100%", border: "1px solid #ccc" }}
      >
        <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={"Aeonik"}>
          Bulk Diamond Point Gift
        </Typography>
        <FormControl component="fieldset">
          <FormLabel component="legend">Upload Mode</FormLabel>
          <RadioGroup
            row
            value={uploadMode}
            onChange={(e) => setUploadMode(e.target.value)}
          >
            <FormControlLabel
              value="phone"
              control={<Radio />}
              label="Add Manually"
            />
            <FormControlLabel
              value="file"
              control={<Radio />}
              label="File Upload"
            />
          </RadioGroup>
        </FormControl>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={
                <>
                  Validity Unit <span style={{ color: "red" }}>*</span>
                </>
              }
              size="small"
              value={bulkGiftType}
              onChange={(e) => setBulkGiftType(e.target.value)}
              fullWidth
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              type="number"
              label={
                <>
                  Validity <span style={{ color: "red" }}>*</span>
                </>
              }
              size="small"
              value={bulkGiftUnit}
              onChange={(e) => setBulkGiftUnit(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Note"
              size="small"
              value={bulkGiftNote}
              onChange={(e) => setBulkGiftNote(e.target.value)}
              fullWidth
            />
          </Grid>
          {uploadMode === "file" && (
            <Grid item xs={12}>
              <Typography fontWeight={500} mb={1} sx={{ pr: 50 }}>
                Upload CSV / Excel File
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <input
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={(e) => setBulkGiftFile(e.target.files[0])}
              />
            </Grid>
          )}
          {uploadMode === "phone" && (
            <Grid container spacing={2} >
              <Grid item xs={12} md={5}>
                <TextField
                  size="small"
                  label="Enter Mobile Number"
                  type="number"
                  value={bulkMobile}
                  onChange={(e) => {
                    let value = e.target.value
                      .replace(/\D/g, "")
                      .replace(/^0+/, "");
                    if (value.length > 10) value = value.slice(0, 10);
                    setBulkMobile(value);
                  }}
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  label="Points"
                  type="number"
                  value={bulkPoints}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "");
                    setBulkPoints(val);
                  }}
                  fullWidth
                  inputProps={{ inputMode: "numeric", maxLength: 5 }}
                />
              </Grid>

              <Grid item xs={12} md={1} >
                <Typography fontWeight={500}>Add</Typography>
                <IconButton
                  onClick={handleBulkAddMobile}
                  disabled={
                    !bulkMobile ||
                    bulkMobile.length !== 10 ||
                    bulkMobileList.some(
                      (item) => item.mobile === bulkMobile
                    )
                  }
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              <Grid item xs={12}>
                <List dense>
                  {bulkMobileList.map((item, idx) => (
                    <ListItem key={idx} disableGutters>
                      {editIndex === idx ? (
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              fullWidth
                              value={editData.mobile}
                              onChange={(e) =>
                                handleEditChange("mobile", e.target.value)
                              }
                              inputProps={{ maxLength: 10 }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              size="small"
                              fullWidth
                              value={editData.points}
                              onChange={(e) =>
                                handleEditChange("points", e.target.value)
                              }
                              inputProps={{ inputMode: "numeric" }}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Box display="flex" justifyContent="flex-end">
                              <IconButton onClick={handleSaveEdit}>
                                <SaveIcon />
                              </IconButton>
                              <IconButton onClick={handleCancelEdit}>
                                <CancelIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={10}>
                            <Typography>
                              <strong>Mobile:</strong> {item.mobile} &nbsp; |
                              &nbsp; <strong>Points:</strong> {item.points}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Box display="flex" justifyContent="flex-end">
                              <IconButton onClick={() => handleEdit(idx)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  handleBulkRemoveMobile(item.mobile)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            {(canCreateSetting || canEdit) && (
              <Button
                variant="contained"
                onClick={
                  uploadMode === "phone"
                    ? handleBulkDiamondGiftByPhoneSubmit
                    : handleBulkDiamondGiftByFileSubmit
                }
                sx={{ backgroundColor: "#752ADA" }}
                disabled={isSavingBulkGift}
              >
                {isSavingBulkGift ? "Uploading..." : "Upload & Gift"}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default BulkDiamondPoints;
